import { Alert, Button, Empty, Skeleton, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

import { EntityLayout } from '@/Ship/Components/EntityCard'
import { classNames } from '@/Ship/Helpers/classNames'

import { REPORT_LIST } from '../Configs/reportList'
import { ReportPeriodFilter } from './ReportPeriodFilter'
import { ReportTabs } from './ReportTabs'
import { useReportPeriod } from './useReportPeriod'

import type { ReportStore } from '../Stores/ReportStore'
import type { ReactNode } from 'react'

import styles from './Report.module.css'

const { Title } = Typography

/** Порожній звіт — стан даних, а не підказка (`SPEC-027` §6). */
export const REPORT_EMPTY_TEXT = 'За обраний період даних немає.'

export interface ReportFrameProps<Data> {
    /** Ключ сторінки з `reportList` — активна вкладка й заголовок вкладки браузера. */
    reportKey: string
    /** Стор звіту; поле сортування рамці байдуже — бере лише стан і дві дії. */
    store: Pick<ReportStore<Data, string>, 'data' | 'error' | 'isLoading' | 'setPeriod' | 'reload'>
    /** Підказка звіту (`SPEC-027` §7). */
    hint: ReactNode
    isEmpty: (data: Data) => boolean
    children: (data: Data) => ReactNode
}

/**
 * Спільна рамка трьох звітів: шапка «Звіти» з вкладками, фільтр періоду,
 * підказка й стани даних.
 *
 * Стани (ЗВ-09, К-07): перше завантаження — скелетон; помилка — `Alert` із
 * текстом сервера й «Повторити», скелетон зникає; порожньо — `Empty`; далі —
 * таблиця (перечитування за сортуванням малює спінер самої таблиці).
 */
function ReportFrameView<Data>({ reportKey, store, hint, isEmpty, children }: ReportFrameProps<Data>) {
    const [period, setPeriod] = useReportPeriod()
    const title = REPORT_LIST.find((item) => item.key === reportKey)?.title ?? 'Звіти'

    useEffect(() => {
        // Ядровий `SetDocumentTitle` ставить заголовок лише ресурсам
        document.title = title
    }, [title])

    useEffect(() => {
        void store.setPeriod(period)
    }, [store, period])

    const { data, error, isLoading } = store

    let content: ReactNode

    if (error !== null) {
        content = (
            <Alert
                type="error"
                showIcon
                title={error}
                data-testid="report-error"
                action={
                    <Button size="small" onClick={() => void store.reload()}>
                        Повторити
                    </Button>
                }
            />
        )
    } else if (data === null) {
        content = isLoading ? <Skeleton active data-testid="report-loading" /> : null
    } else if (isEmpty(data)) {
        content = <Empty description={REPORT_EMPTY_TEXT} data-testid="report-empty" />
    } else {
        content = children(data)
    }

    return (
        <div className={classNames(styles['page'])}>
            <EntityLayout>
                <div className={classNames(styles['hero'])}>
                    <Title level={3} className={classNames(styles['title'])}>
                        Звіти
                    </Title>
                    <ReportTabs activeKey={reportKey} />
                </div>
                <div className={classNames(styles['panel'])}>
                    <ReportPeriodFilter period={period} onChange={setPeriod} />
                    {hint}
                    {content}
                </div>
            </EntityLayout>
        </div>
    )
}

export const ReportFrame = observer(ReportFrameView) as typeof ReportFrameView
